<template>
  <button
    class="audio-playlist-item"
    :class="{
      'audio-playlist-item--active': isActive,
      'audio-playlist-item--playing': isActive && isPlaying,
    }"
    type="button"
    :aria-label="`${title}${formattedDuration ? `, ${formattedDuration}` : ''}${isActive && isPlaying ? ', playing' : isActive ? ', paused' : ''}`"
    :aria-current="isActive ? 'true' : undefined"
    @click="emit('select')"
  >
    <!-- Track number or equalizer indicator -->
    <div class="audio-playlist-item__number">
      <template v-if="isActive && isPlaying">
        <div class="audio-playlist-item__equalizer" aria-hidden="true">
          <span class="audio-playlist-item__equalizer-bar" />
          <span class="audio-playlist-item__equalizer-bar" />
          <span class="audio-playlist-item__equalizer-bar" />
        </div>
      </template>
      <span v-else class="audio-playlist-item__index">{{ index }}</span>
    </div>

    <!-- Thumbnail -->
    <div class="audio-playlist-item__thumbnail">
      <img
        v-if="thumbnailUrl"
        :src="thumbnailUrl"
        :alt="title"
        class="audio-playlist-item__image"
        loading="lazy"
      />
      <div v-else class="audio-playlist-item__placeholder">
        <v-icon size="24" color="grey" :icon="mdiMusicNote" />
      </div>

      <!-- Play indicator overlay -->
      <div v-if="isActive" class="audio-playlist-item__indicator">
        <v-icon v-if="isPlaying" size="20" color="white" :icon="mdiVolumeHigh" />
        <v-icon v-else size="20" color="white" :icon="mdiPause" />
      </div>
    </div>

    <!-- Info -->
    <div class="audio-playlist-item__info">
      <span class="audio-playlist-item__title">{{ title }}</span>
      <span v-if="description" class="audio-playlist-item__description">{{ description }}</span>
    </div>

    <!-- Duration -->
    <span class="audio-playlist-item__duration">{{ formattedDuration }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiMusicNote, mdiVolumeHigh, mdiPause } from '@mdi/js'
import { formatTime } from '../utils/audio-utils'

const props = defineProps<{
  index: number
  title: string
  thumbnailUrl?: string
  description?: string
  duration: number
  isActive?: boolean
  isPlaying?: boolean
}>()

const emit = defineEmits<{
  select: []
}>()

const formattedDuration = computed(() => formatTime(props.duration))
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.audio-playlist-item {
  display: flex;
  align-items: center;
  gap: $space-md;
  width: 100%;
  padding: $space-sm $space-md;
  background: transparent;
  border: none;
  border-radius: $radius-md;
  cursor: pointer;
  text-align: left;
  @include transition-fast(background-color);

  @include focus-visible-only;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.05);
  }

  &--active {
    background: rgba(var(--v-theme-primary), 0.1);

    &:hover {
      background: rgba(var(--v-theme-primary), 0.15);
    }
  }

  &--playing {
    .audio-playlist-item__title {
      font-weight: $font-semibold;
    }
  }

  &__number {
    flex-shrink: 0;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__index {
    font-size: $text-sm;
    font-weight: $font-medium;
    font-variant-numeric: tabular-nums;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__equalizer {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 16px;
  }

  &__equalizer-bar {
    display: block;
    width: 3px;
    background: var(--audio-accent-color, rgb(var(--v-theme-primary)));
    border-radius: 1px;
    animation: equalizer 1.2s ease-in-out infinite;

    &:nth-child(1) {
      height: 60%;
      animation-delay: 0s;
    }

    &:nth-child(2) {
      height: 100%;
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      height: 40%;
      animation-delay: 0.4s;
    }

    @include reduced-motion {
      animation: none;
      height: 60% !important;
    }
  }

  &__thumbnail {
    position: relative;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: $radius-sm + 2px;
    overflow: hidden;
    background: rgba(var(--v-theme-on-surface), 0.05);
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-on-surface), 0.05);
  }

  &__indicator {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-size: $text-md;
    font-weight: $font-medium;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__description {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__duration {
    flex-shrink: 0;
    font-size: $text-sm;
    font-weight: $font-medium;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary + 0.1);
    font-variant-numeric: tabular-nums;
  }
}

@keyframes equalizer {
  0%,
  100% {
    height: 20%;
  }
  50% {
    height: 100%;
  }
}
</style>
